import { useEffect } from "react";
import { Link } from "wouter";
import {
  GRADE_DEFECT_CEILINGS,
  GRADE_DESCRIPTIONS,
  SCALE_EFFECTIVE_DATE_LABEL,
} from "@shared/grading";

export default function TermsPage() {
  useEffect(() => {
    document.title = "Terms of Service | OpenBox by ShopJimmy";
    return () => {
      document.title = "OpenBox by ShopJimmy";
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      const t = window.setTimeout(tryScroll, 100);
      return () => window.clearTimeout(t);
    }
  }, []);

  return (
    <div className="bg-background min-h-screen">
      <div className="container-responsive max-w-4xl py-10 md:py-14">
        <div className="mb-8">
          <h1
            className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-2"
            data-testid="text-terms-heading"
          >
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="text-terms-effective">
            Effective {SCALE_EFFECTIVE_DATE_LABEL}
          </p>
        </div>

        <div className="prose dark:prose-invert max-w-none space-y-8 text-sm md:text-base leading-relaxed">
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground mt-0">
              1. About OpenBox by ShopJimmy
            </h2>
            <p>
              OpenBox by ShopJimmy ("we", "us", "OpenBox") is a B2B and consumer marketplace
              operated by ShopJimmy.com, LLC for open-box and refurbished appliances. By using
              this site, placing an order, or creating an account you agree to these terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              2. Cosmetic Grading Scale
            </h2>
            <p>
              Every refrigerator we sell is fully functional, factory-tested, and ships with the
              manufacturer's standard warranty. Cosmetic grades describe the condition of
              <strong> front-facing surfaces only</strong>. Side and back panels, internal
              fixtures, and packaging may show additional handling marks regardless of grade.
            </p>

            <div className="not-prose rounded-lg border border-border overflow-hidden mt-4 mb-4">
              <table className="w-full text-sm" data-testid="table-grading-scale">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3 font-semibold">Grade</th>
                    <th className="text-left p-3 font-semibold">Front-facing defects</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border">
                    <td className="p-3 font-bold">A</td>
                    <td className="p-3">{GRADE_DEFECT_CEILINGS.A}</td>
                    <td className="p-3">{GRADE_DESCRIPTIONS.A.long}</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-bold">A-</td>
                    <td className="p-3">{GRADE_DEFECT_CEILINGS['A-']}</td>
                    <td className="p-3">{GRADE_DESCRIPTIONS['A-'].long}</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-bold">B+</td>
                    <td className="p-3">Up to {GRADE_DEFECT_CEILINGS['B+']}</td>
                    <td className="p-3">{GRADE_DESCRIPTIONS['B+'].long}</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-bold">B-</td>
                    <td className="p-3">Up to {GRADE_DEFECT_CEILINGS['B-']}</td>
                    <td className="p-3">{GRADE_DESCRIPTIONS['B-'].long}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              The current grading scale took effect on{' '}
              <strong>{SCALE_EFFECTIVE_DATE_LABEL}</strong>. Orders placed before that date were
              graded under the prior scale (B+ ≤ 3 defects, B- ≤ 5 defects). Item-specific videos
              and photos posted on each product page show the actual unit you will receive — we
              encourage you to review them before purchasing.
            </p>

            <p>
              See the{' '}
              <Link href="/grading" className="text-primary underline" data-testid="link-grading-page">
                Grading &amp; Packaging
              </Link>{' '}
              page for examples and additional detail.
            </p>
          </section>

          <section id="data-accuracy">
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              3. Product Data &amp; Image Accuracy
            </h2>
            <p>
              Product images, specifications, dimensions, feature lists, and included
              accessories shown on this site come from a combination of supplier-provided
              materials, manufacturer documentation, and AI-assisted image and specification
              discovery tools. We work hard to surface accurate, complete information for
              every model we list, and we believe the details on each product page are
              correct to the best of our knowledge.
            </p>
            <p>
              That said, because much of this content is sourced or auto-discovered rather
              than authored by us directly, we cannot guarantee that every image, spec,
              dimension, or accessory list is 100% complete or perfectly representative of
              the specific unit you receive. Stock photography may show options or
              accessories that are not included with an open-box unit, and AI-generated
              specs may occasionally differ from the manufacturer's most current
              documentation. We strongly encourage customers to research the model number
              independently (for example, on the manufacturer's website) before purchasing,
              and — for local customers — to schedule a visit to inspect units in person to
              confirm condition and included accessories. Item-specific videos and photos
              on each product page show the actual unit you will receive whenever
              available.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              4. Orders, Pricing &amp; Availability
            </h2>
            <p>
              All inventory is sold on a first-come, first-served basis. Prices, freight quotes,
              and availability are subject to change until an order is paid in full. Sales are
              FOB Burnsville, MN unless otherwise stated.
            </p>
            <p>
              <strong>All sales are final.</strong> Returns, refunds, and credits are reviewed
              on a case-by-case basis and may only be initiated by an OpenBox account
              representative.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">5. Warranty</h2>
            <p>
              Refrigerators include a 1-Year Factory Warranty from the original manufacturer.
              Warranty service is fulfilled directly by the manufacturer; OpenBox does not
              extend or modify factory warranty terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">6. Account Use</h2>
            <p>
              You are responsible for keeping your login credentials secure and for all activity
              under your account. Business accounts must be used for legitimate resale or
              wholesale purposes consistent with the application you submitted.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              7. Changes to These Terms
            </h2>
            <p>
              We may update these terms from time to time. When we change the cosmetic grading
              scale, we publish a banner on the site, surface a notice on order and invoice
              emails for at least 60 days, and update the effective date at the top of this
              page.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">8. Contact</h2>
            <p>
              Questions about these terms? Email{' '}
              <a
                href="mailto:openbox@shopjimmy.com"
                className="text-primary underline"
                data-testid="link-contact-email"
              >
                openbox@shopjimmy.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
